(() => {
  const aecRoot = document.querySelector(".aec-v2");
  if (!aecRoot || aecRoot.dataset.enhanced === "true") return;
  aecRoot.dataset.enhanced = "true";

  const aecAssetBase = aecRoot.dataset.assetBase || "";
  function aecAsset(name) {
    return aecAssetBase + "aec-v2-" + name;
  }

function lockVideoToSilentPlayback(video) {
  if (!(video instanceof HTMLVideoElement) || video.dataset.silentPlayback === "true") return;

  video.dataset.silentPlayback = "true";
  video.defaultMuted = true;
  video.muted = true;
  video.volume = 0;
  video.setAttribute("muted", "");

  video.addEventListener("volumechange", function () {
    if (!video.muted) video.muted = true;
    if (video.volume !== 0) video.volume = 0;
  });
}

aecRoot.querySelectorAll("video").forEach(lockVideoToSilentPlayback);

const silentVideoObserver = new MutationObserver(function (records) {
  records.forEach(function (record) {
    record.addedNodes.forEach(function (node) {
      if (!(node instanceof Element)) return;
      if (node.matches("video")) lockVideoToSilentPlayback(node);
      node.querySelectorAll("video").forEach(lockVideoToSilentPlayback);
    });
  });
});

silentVideoObserver.observe(aecRoot, { childList: true, subtree: true });

class RealseeVRFrame extends HTMLElement {
  constructor() {
    super();
    this.iframe = null;
    this.isIntersecting = false;
    this.syncFrame = null;
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    if (this.isConnectedToPage) return;
    this.isConnectedToPage = true;
    this.viewportObserver = new IntersectionObserver((entries) => {
      this.isIntersecting = Boolean(entries[0] && entries[0].isIntersecting);
      this.requestLoad();
    }, { threshold: [0, 0.01] });
    this.viewportObserver.observe(this);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.requestLoad();
  }

  disconnectedCallback() {
    this.viewportObserver?.disconnect();
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.unmountIframe();
    this.isConnectedToPage = false;
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.unmountIframe();
      return;
    }
    this.requestLoad();
  }

  requestLoad() {
    if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
    this.syncFrame = requestAnimationFrame(() => {
      this.syncFrame = null;
      if (this.shouldLoad()) this.mountIframe();
      else this.unmountIframe();
    });
  }

  shouldLoad() {
    if (!this.dataset.src || document.hidden || !this.isIntersecting) return false;
    const outputPanel = this.closest(".output-visual");
    return !outputPanel || outputPanel.getAttribute("aria-hidden") !== "true";
  }

  mountIframe() {
    if (this.iframe) return;
    const iframe = document.createElement("iframe");
    iframe.src = this.dataset.src;
    iframe.width = "100%";
    iframe.height = "100%";
    iframe.loading = "eager";
    iframe.allow = "microphone; camera";
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = "strict-origin-when-cross-origin";
    iframe.title = this.dataset.title || "Realsee 3D Tour";
    iframe.addEventListener("load", () => this.setAttribute("loaded", ""), { once: true });
    this.appendChild(iframe);
    this.iframe = iframe;
  }

  unmountIframe() {
    if (!this.iframe) return;
    this.iframe.removeAttribute("src");
    this.iframe.remove();
    this.iframe = null;
    this.removeAttribute("loaded");
  }
}

if (!customElements.get("realsee-vr-frame")) {
  customElements.define("realsee-vr-frame", RealseeVRFrame);
}

const capabilityStates = {
  capture: {
    state: "CAPTURE / EXISTING CONDITIONS",
    title: "A navigable record of the real site",
    image: aecAsset("l01-capture-existing-conditions.webp"),
    alt: "Animated Realsee 3D Tour of captured existing conditions with on-site dimensions and annotations"
  },
  prepare: {
    state: "PREPARE / DESIGN BASE",
    title: "Project files built from captured conditions",
    image: aecAsset("l02-prepare-site-tour-animated.webp"),
    alt: "Animated Realsee 3D Tour of captured renovation conditions with visible ceiling services, walls, and openings"
  },
  verify: {
    state: "VERIFY / VISIBLE CONDITIONS",
    title: "Design intent reviewed against the real space",
    image: aecAsset("l03-verify-site-vs-design.webp"),
    alt: "Side-by-side comparison of a captured kitchen and its design model"
  },
  track: {
    state: "TRACK / PROJECT CONTEXT",
    title: "Progress and questions connected to place",
    image: aecAsset("l04-track-annotations.webp"),
    alt: "Captured renovation site with location-based annotations and project discussion"
  }
};

const roles = {
  designer: {
    image: aecAsset("r01-architect-designer.webp"),
    imagePosition: "center 50%",
    alt: "Architect reviewing a captured kitchen point cloud, floor plan, and BIM model",
    media: "Design review · Captured site to design base",
    eyebrow: "FOR ARCHITECTS AND DESIGNERS",
    title: "Design From a Site Record You Can Revisit",
    description: "Start planning from captured existing conditions instead of rebuilding the site from old drawings, phone photos, and separate measurement notes.",
    points: [
      "Prepare a usable base for early layout and design.",
      "Choose point cloud, RVT, SketchUp, or Manual CAD when more detail is needed.",
      "Compare design intent with the visible site before decisions move forward."
    ],
    link: "https://www.realsee.ai/pages/architect-designer",
    linkLabel: "Explore Realsee for architects and designers"
  },
  contractor: {
    image: aecAsset("r02-contractor-installer.webp"),
    imagePosition: "center 50%",
    alt: "Cabinet installer checking a wall opening with a laser measure and captured site data",
    media: "Pre-installation review · Visible site conditions",
    eyebrow: "FOR CONTRACTORS AND INSTALLERS",
    title: "Review the Site Before Work Is Committed",
    description: "Check visible conditions before final scope, fabrication, delivery, or installation so design, supply, and field teams can discuss the same space.",
    points: [
      "Review walls, openings, installation surfaces, equipment zones, and access routes.",
      "Use the Realsee 3D Tour, Manual CAD, elevations, and sections during scope and installation planning.",
      "Keep final measurement, shop drawings, fabrication, and installation responsibility with the relevant trade."
    ]
  },
  manager: {
    image: aecAsset("r03-project-manager.webp"),
    imagePosition: "center 50%",
    alt: "Project manager and contractor comparing progress inside a small retail renovation",
    media: "Field coordination · Progress and site context",
    eyebrow: "FOR PROJECT MANAGERS",
    title: "Coordinate Teams Around the Real Site",
    description: "Give design, field, and supply teams a shared reference for scope conversations, progress reviews, and questions that need spatial context.",
    points: [
      "Repeat captures when site conditions or construction stages change.",
      "Review progress through timeline, comparison, and annotations.",
      "Open the Realsee 3D Tour from Autodesk Forma / ACC dashboards through the Realsee Card."
    ]
  },
  owner: {
    image: aecAsset("r04-owner.webp"),
    imagePosition: "center 50%",
    alt: "Property owner remotely reviewing a Realsee 3D Tour and project timeline",
    media: "Remote project review · Site timeline",
    eyebrow: "FOR OWNERS",
    title: "See the Site Without a Photo Thread",
    description: "Review existing conditions, design context, and progress remotely while preserving a consistent visual record for each project or location.",
    points: [
      "Walk the site remotely in a Realsee 3D Tour.",
      "Compare project stages without reconstructing updates from separate messages.",
      "Keep a location-by-location record for future review and renovation planning."
    ]
  }
};

const outputs = {
  review: {
    kind: "PROJECT REVIEW",
    title: "Comparison / Timeline / Annotations",
    description: "Compare project stages and keep visible field conditions, questions, and decisions connected to place.",
    meta: "Progress · Field questions · Decisions",
    action: "View Demo",
    actionType: "external",
    actionUrl: "https://realsee.ai/z7RRkR4D",
    downloadUrl: "",
    telemetryEvent: "marketingEvent",
    telemetryUid: "pp:page:aec:project_addons:review_vr:view_demo"
  },
  cloud: {
    kind: "SPATIAL DATA",
    title: "Point Cloud",
    description: "Use E57 / LAS / PLY / RCP files in modeling, dimensional review, and downstream software workflows.",
    meta: "E57 / LAS / PLY / RCP · Modeling · Dimensional review",
    action: "Download Sample",
    actionType: "download",
    downloadUrl: "https://global-public.realsee-cdn.com/release/web/aec-samples/realsee-pointcloud-sample.a19a46e9.e57",
    telemetryEvent: "downloadClick",
    telemetryUid: "pp:page:aec:project_addons:point_cloud:download"
  },
  rvt: {
    kind: "BIM MODEL",
    title: "RVT / Scan-to-BIM",
    description: "Use the Revit model for renovation design and BIM/VDC workflows.",
    meta: "LOD 200 / LOA 20 available · Design · BIM/VDC",
    action: "Download Sample",
    actionType: "download",
    downloadUrl: "https://global-public.realsee-cdn.com/release/web/aec-samples/bim-model-revit.c3a6a2d1.rvt",
    telemetryEvent: "downloadClick",
    telemetryUid: "pp:page:aec:project_addons:scan_to_bim:download"
  },
  cad: {
    kind: "INSTANT CAD",
    title: "Instant CAD",
    description: "Generate a floor plan automatically for early layout planning, scope review, estimating, and design.",
    meta: "Auto-generated floor plan · 1 hour",
    action: "Download Sample",
    actionType: "download",
    downloadUrl: "https://global-public.realsee-cdn.com/release/web/aec-samples/auto-cad.bddb46c0.dxf",
    telemetryEvent: "downloadClick",
    telemetryUid: "pp:page:aec:project_addons:instant_cad:download"
  },
  rcp: {
    kind: "MANUAL CAD",
    title: "Floor Plan, Reflected Ceiling Plan",
    description: "Order Manual CAD floor plans and reflected ceiling plans for layout, lighting, soffits, and other visible overhead conditions.",
    meta: "Manual CAD · 3–5 business days",
    action: "Download Sample",
    actionType: "download",
    downloadUrl: "https://global-public.realsee-cdn.com/release/web/aec-samples/manual-cad-floor-plan-and-reflected-ceiling-plan.4010a462.zip",
    telemetryEvent: "downloadClick",
    telemetryUid: "pp:page:aec:project_addons:manual_cad_floor_plan_rcp:download"
  },
  elevation: {
    kind: "MANUAL CAD",
    title: "Elevation / Section",
    description: "Review openings, wall conditions, cabinetry, equipment, and vertical installation relationships.",
    meta: "Manual CAD · 3–5 business days",
    action: "Download Sample",
    actionType: "download",
    downloadUrl: "https://global-public.realsee-cdn.com/release/web/aec-samples/manual-cad-elevation.ee928b4b.zip",
    telemetryEvent: "downloadClick",
    telemetryUid: "pp:page:aec:project_addons:manual_cad_elevation_section:download"
  },
  sketchup: {
    kind: "3D MODEL",
    title: "SketchUp",
    description: "Move existing conditions into spatial design, option review, and client presentations.",
    meta: "Design · Visualization",
    action: "Download Sample",
    actionType: "download",
    downloadUrl: "https://global-public.realsee-cdn.com/release/web/aec-samples/cad-model-sketchup.2af8625d.skp",
    telemetryEvent: "downloadClick",
    telemetryUid: "pp:page:aec:project_addons:sketchup:download"
  }
};

const demos = {
  compare: {
    eyebrow: "SIDE-BY-SIDE COMPARISON",
    title: "Review Field Differences in Context",
    copy: "Compare the current site record with a design model, previous stage, or updated capture without separating the question from the real space."
  },
  timeline: {
    eyebrow: "TIMELINE PROGRESS REVIEW",
    title: "Move Through the Project Stage by Stage",
    copy: "Navigate capture milestones along one timeline so owners, project managers, contractors, and suppliers can understand what changed and when."
  },
  annotations: {
    eyebrow: "LOCATION-BASED ANNOTATIONS",
    title: "Keep Every Question Tied to Place",
    copy: "Add installation context and field observations directly to the visual site record instead of circulating isolated screenshots."
  },
  acc: {
    eyebrow: "AUTODESK FORMA / ACC",
    title: "Bring Site Context Into Coordination",
    copy: "Add the Realsee Card to an Autodesk Forma / ACC dashboard so project teams can open the same Realsee 3D Tour from their existing project workspace."
  }
};

const scenarios = {
  home: {
    image: aecAsset("s01-home-renovation.webp"),
    alt: "Remodeling surveyor capturing an older home with a Galois P4 LiDAR scanner",
    media: "HOME RENOVATION & REMODELING",
    roles: "ARCHITECT / DESIGNER + CONTRACTOR / REMODELER",
    title: "From Site Capture to an Instant CAD Floor Plan",
    problem: "Older homes often have missing or outdated drawings, so early layouts and scope decisions depend on separate photos, notes, and return visits.",
    solution: "Capture the existing space quickly and get an Instant CAD Floor Plan in 1 hour for early layout and design work.",
    list: [
      "Review the home remotely before the next design conversation.",
      "Use point cloud, RVT, SketchUp, or Manual CAD when more detail is needed.",
      "Share the same visual record while the trade retains responsibility for final field measurement and verification."
    ]
  },
  kitchen: {
    image: aecAsset("s02-kitchen-cabinetry-v2.webp"),
    alt: "Kitchen designer reviewing documented dimensions for a custom cabinet opening",
    hasEmbeddedMediaLabel: true,
    media: "KITCHEN, BATH, CABINETRY & CUSTOM FIT-OUT",
    roles: "DESIGNER + CONTRACTOR / INSTALLER",
    title: "Review the Room Before Custom Orders",
    problem: "Cabinetry, millwork, countertops, fixtures, and appliances are planned against the actual conditions of walls, floors, ceilings, openings, and installation surfaces.",
    solution: "Use the Realsee 3D Tour, a Manual CAD Floor Plan, elevations, sections, SketchUp, and comparison tools before made-to-order components move into fabrication or installation.",
    list: [
      "Keep design and supplier conversations tied to the same visible site conditions.",
      "Use elevations and sections to review vertical relationships and installation surfaces.",
      "Keep final measurement, shop drawings, fabrication, and installation responsibility with the relevant trade."
    ]
  },
  commercial: {
    image: aecAsset("s03-commercial-kitchen.webp"),
    alt: "Commercial kitchen renovation team reviewing equipment and service conditions",
    media: "COMMERCIAL KITCHEN RENOVATION",
    roles: "CONTRACTOR / INSTALLER + PROJECT MANAGER",
    title: "Coordinate Trades Around One Site Record",
    problem: "Foodservice consultants, contractors, equipment suppliers, and installers often coordinate from separate drawings, photos, and site notes.",
    solution: "Give the team one visual and spatial record for reviewing equipment zones, access, installation interfaces, and project changes together.",
    list: [
      "Use the Realsee 3D Tour, point-cloud files, CAD floor plans, reflected ceiling plans, elevations, and sections as shared project references.",
      "Add annotations where a supplier or installer needs field context.",
      "Open the Realsee 3D Tour from an Autodesk Forma / ACC dashboard through the Realsee Card."
    ]
  },
  rollout: {
    image: aecAsset("s04-multi-site-rollout-no-hardhat.webp"),
    alt: "Rollout project manager and contractor reviewing a small retail fit-out",
    media: "MULTI-SITE RETAIL & SMALL COMMERCIAL ROLLOUT",
    roles: "PROJECT MANAGER + OWNER",
    title: "Standardize Every Site Record",
    problem: "Retail, restaurant, clinic, and small office programs repeat a design across locations with different existing conditions, vendors, and schedules.",
    solution: "Apply the same capture and deliverable standard to each location, then use the Realsee 3D Tour, timeline, comparison, and annotations to review progress and preserve the site record.",
    list: [
      "Give project teams the same type of site record at every location.",
      "Compare visible conditions and progress across active sites.",
      "Preserve a location-by-location record for future review and renovation planning."
    ]
  }
};

const specialties = {
  window: {
    image: aecAsset("t01-window-door-v2.webp"),
    alt: "Exposed window opening documented with visible height and width measurements",
    hasEmbeddedVisualLabel: true,
    kicker: "WINDOW & DOOR REPLACEMENT",
    title: "See Every Opening in Context",
    copy: "Document each opening and surrounding conditions so installers can review access, trim, flashing, and nearby constraints before final measurement and ordering.",
    boundary: "Realsee does not replace professional measurement, structural judgment, or installation responsibility."
  },
  millwork: {
    image: aecAsset("t02-millwork-cabinetry-v2.webp"),
    alt: "Millworker verifying a custom built-in alcove before fabrication",
    kicker: "MILLWORK & CABINETRY",
    title: "Review Fit Before Fabrication",
    copy: "Provide site context for field measurement and shop drawing review using Manual CAD, elevations, sections, and the Realsee 3D Tour with annotations.",
    boundary: "Fabricators and installers retain responsibility for shop drawings, measurements, fabrication decisions, and final fit."
  },
  deck: {
    image: aecAsset("t03-deck-outdoor-clean-v4.webp"),
    alt: "Deck contractor checking ledger, grade, drainage, and access conditions",
    kicker: "DECK & OUTDOOR LIVING",
    title: "Bring Grade and Access Into View",
    copy: "Document the exterior, attachment area, grade changes, drainage, nearby openings, and construction access so the design and build team can review visible conditions earlier.",
    boundary: "Realsee does not replace permitting, structural design, engineering calculations, or code review."
  },
  pool: {
    image: aecAsset("t04-pool-backyard-p4-rtk-v4.webp"),
    alt: "Pool contractors reviewing a P4 scan of backyard grades, equipment, and access",
    kicker: "POOL & BACKYARD",
    title: "Document the Whole Installation Zone",
    copy: "Document yard dimensions, grades, access paths, equipment zones, fencing, and surrounding conditions for design review and progress records.",
    boundary: "Licensed professionals remain responsible for design, safety, drainage, engineering, and permitting."
  },
  fence: {
    image: aecAsset("t05-fence-p4-rtk-v2.webp"),
    alt: "Fence installer using a P4 scan to document a sloped run and gate opening",
    kicker: "FENCE INSTALLATION",
    title: "Clarify Runs, Gates, and Site Constraints",
    copy: "Record fence runs, gate locations, slopes, obstacles, adjacent structures, and visible site conditions for clearer scope discussion.",
    boundary: "Realsee does not replace property surveys, utility locating, easement review, or permit requirements."
  }
};

const p4HeroVideo = aecRoot.querySelector("#p4-hero-video");
const p4HeroCanvas = aecRoot.querySelector("#p4-hero-canvas");
const p4VideoShell = aecRoot.querySelector("#p4-video-shell");
const p4VideoToggle = aecRoot.querySelector("#p4-video-toggle");

if (p4HeroVideo && p4HeroCanvas && p4VideoShell && p4VideoToggle) {
  const pauseIcon = p4VideoToggle.querySelector(".video-icon-pause");
  const playIcon = p4VideoToggle.querySelector(".video-icon-play");
  const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  let heroIsVisible = true;
  let userPaused = false;
  let gl;
  let texture;
  let frameRequest = 0;
  let lastRenderedTime = -1;
  let webglReady = false;
  let firstFrameRendered = false;

  function motionIsSuppressed() {
    return motionPreference.matches || saveData;
  }

  function cancelRenderLoop() {
    if (!frameRequest) return;
    window.cancelAnimationFrame(frameRequest);
    frameRequest = 0;
  }

  function fallbackToPoster() {
    cancelRenderLoop();
    webglReady = false;
    p4VideoShell.classList.remove("is-ready");
    p4VideoShell.classList.add("is-error");
    p4VideoToggle.hidden = true;
    if (!p4HeroVideo.paused) p4HeroVideo.pause();
  }

  function createShader(type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const message = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(message || "Unable to compile the P4 transparency shader.");
    }
    return shader;
  }

  function initializeKeyRenderer() {
    try {
      gl = p4HeroCanvas.getContext("webgl", {
        alpha: true,
        antialias: true,
        premultipliedAlpha: false,
        preserveDrawingBuffer: false
      });
      if (!gl) return false;

      const vertexShader = createShader(gl.VERTEX_SHADER, [
        "attribute vec2 a_position;",
        "varying vec2 v_uv;",
        "void main() {",
        "  gl_Position = vec4(a_position, 0.0, 1.0);",
        "  v_uv = (a_position + 1.0) * 0.5;",
        "}"
      ].join("\n"));
      const fragmentShader = createShader(gl.FRAGMENT_SHADER, [
        "precision mediump float;",
        "uniform sampler2D u_texture;",
        "varying vec2 v_uv;",
        "float colorDistanceAt(vec2 uv) {",
        "  vec3 color = texture2D(u_texture, clamp(uv, vec2(0.0), vec2(1.0))).rgb;",
        "  return length(color) / 1.7320508;",
        "}",
        "float foregroundSupport(float value) {",
        "  return smoothstep(0.010, 0.040, value);",
        "}",
        "void main() {",
        "  vec4 source = texture2D(u_texture, v_uv);",
        "  float colorDistance = length(source.rgb) / 1.7320508;",
        "  float colorAlpha = smoothstep(0.025, 0.070, colorDistance);",
        "  float leftDistance = max(max(colorDistanceAt(v_uv - vec2(0.018, 0.0)), colorDistanceAt(v_uv - vec2(0.033, 0.0))), max(colorDistanceAt(v_uv - vec2(0.055, 0.0)), colorDistanceAt(v_uv - vec2(0.090, 0.0))));",
        "  float rightDistance = max(max(colorDistanceAt(v_uv + vec2(0.018, 0.0)), colorDistanceAt(v_uv + vec2(0.033, 0.0))), max(colorDistanceAt(v_uv + vec2(0.055, 0.0)), colorDistanceAt(v_uv + vec2(0.090, 0.0))));",
        "  float lowerDistance = max(max(colorDistanceAt(v_uv - vec2(0.0, 0.018)), colorDistanceAt(v_uv - vec2(0.0, 0.033))), max(colorDistanceAt(v_uv - vec2(0.0, 0.055)), colorDistanceAt(v_uv - vec2(0.0, 0.090))));",
        "  float upperDistance = max(max(colorDistanceAt(v_uv + vec2(0.0, 0.018)), colorDistanceAt(v_uv + vec2(0.0, 0.033))), max(colorDistanceAt(v_uv + vec2(0.0, 0.055)), colorDistanceAt(v_uv + vec2(0.0, 0.090))));",
        "  float enclosedX = min(foregroundSupport(leftDistance), foregroundSupport(rightDistance));",
        "  float enclosedY = min(foregroundSupport(lowerDistance), foregroundSupport(upperDistance));",
        "  float enclosed = max(enclosedX, enclosedY);",
        "  float xRegion = smoothstep(0.13, 0.20, v_uv.x) * (1.0 - smoothstep(0.80, 0.87, v_uv.x));",
        "  float yFromTop = 1.0 - v_uv.y;",
        "  float yRegion = smoothstep(0.04, 0.10, yFromTop) * (1.0 - smoothstep(0.55, 0.63, yFromTop));",
        "  float alpha = max(colorAlpha, enclosed * xRegion * yRegion);",
        "  gl_FragColor = vec4(source.rgb, alpha);",
        "}"
      ].join("\n"));
      const program = gl.createProgram();
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || "Unable to link the P4 transparency shader.");
      }

      const vertices = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, vertices);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
        -1, -1,
         1, -1,
        -1,  1,
         1,  1
      ]), gl.STATIC_DRAW);

      gl.useProgram(program);
      const position = gl.getAttribLocation(program, "a_position");
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

      texture = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.uniform1i(gl.getUniformLocation(program, "u_texture"), 0);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.clearColor(0, 0, 0, 0);
      gl.disable(gl.DEPTH_TEST);
      return true;
    } catch (error) {
      console.warn("P4 transparent video rendering is unavailable.", error);
      return false;
    }
  }

  function syncCanvasResolution() {
    const width = p4HeroVideo.videoWidth;
    const height = p4HeroVideo.videoHeight;
    if (!width || !height) return false;
    if (p4HeroCanvas.width !== width || p4HeroCanvas.height !== height) {
      p4HeroCanvas.width = width;
      p4HeroCanvas.height = height;
    }
    gl.viewport(0, 0, p4HeroCanvas.width, p4HeroCanvas.height);
    return true;
  }

  function shouldRender() {
    return webglReady && !motionIsSuppressed() && !userPaused && !document.hidden && heroIsVisible && !p4HeroVideo.paused && !p4HeroVideo.ended;
  }

  function renderVideoFrame() {
    frameRequest = 0;
    if (!shouldRender()) return;

    if (p4HeroVideo.readyState >= 2 && p4HeroVideo.currentTime !== lastRenderedTime && syncCanvasResolution()) {
      try {
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, p4HeroVideo);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        lastRenderedTime = p4HeroVideo.currentTime;
        if (!firstFrameRendered) {
          firstFrameRendered = true;
          p4VideoShell.classList.remove("is-error");
          p4VideoShell.classList.add("is-ready");
        }
      } catch (error) {
        console.warn("P4 transparent video frame could not be rendered.", error);
        fallbackToPoster();
        return;
      }
    }

    if (shouldRender()) frameRequest = window.requestAnimationFrame(renderVideoFrame);
  }

  function startRenderLoop() {
    if (!frameRequest && shouldRender()) frameRequest = window.requestAnimationFrame(renderVideoFrame);
  }

  function updateVideoControl() {
    const isPlaying = !p4HeroVideo.paused && !p4HeroVideo.ended;
    pauseIcon.hidden = !isPlaying;
    playIcon.hidden = isPlaying;
    p4VideoToggle.setAttribute("aria-label", isPlaying ? "Pause product rotation" : "Play product rotation");
  }

  function pauseForContext() {
    if (!p4HeroVideo.paused) p4HeroVideo.pause();
    cancelRenderLoop();
  }

  function playWhenAllowed() {
    if (!webglReady || motionIsSuppressed() || userPaused || document.hidden || !heroIsVisible) return;
    const playback = p4HeroVideo.play();
    if (playback) {
      playback.then(function () {
        p4VideoToggle.hidden = false;
        updateVideoControl();
        startRenderLoop();
      }).catch(function () {
        p4VideoShell.classList.remove("is-ready");
        p4VideoToggle.hidden = false;
        updateVideoControl();
      });
    }
  }

  function applyMotionPreference() {
    if (!webglReady || motionIsSuppressed()) {
      pauseForContext();
      if (p4HeroVideo.readyState) p4HeroVideo.currentTime = 0;
      p4VideoShell.classList.remove("is-ready");
      p4VideoToggle.hidden = true;
      return;
    }
    p4VideoToggle.hidden = p4HeroVideo.readyState < 3;
    playWhenAllowed();
  }

  p4HeroVideo.addEventListener("canplay", function () {
    p4VideoToggle.hidden = !webglReady || motionIsSuppressed();
    playWhenAllowed();
  });

  p4HeroVideo.addEventListener("playing", function () {
    updateVideoControl();
    startRenderLoop();
  });

  p4HeroVideo.addEventListener("pause", function () {
    cancelRenderLoop();
    updateVideoControl();
  });

  p4HeroVideo.addEventListener("error", fallbackToPoster);

  p4HeroCanvas.addEventListener("webglcontextlost", function (event) {
    event.preventDefault();
    fallbackToPoster();
  });

  p4VideoToggle.addEventListener("click", function () {
    if (p4HeroVideo.paused) {
      userPaused = false;
      playWhenAllowed();
      return;
    }
    userPaused = true;
    p4HeroVideo.pause();
  });

  if ("IntersectionObserver" in window) {
    const heroVideoObserver = new IntersectionObserver(function (entries) {
      heroIsVisible = Boolean(entries[0] && entries[0].isIntersecting);
      if (heroIsVisible) playWhenAllowed();
      else pauseForContext();
    }, { threshold: 0.08 });
    heroVideoObserver.observe(aecRoot.querySelector("#top"));
  }

  document.addEventListener("visibilitychange", function () {
    if (document.hidden) pauseForContext();
    else playWhenAllowed();
  });

  if (motionPreference.addEventListener) motionPreference.addEventListener("change", applyMotionPreference);
  else motionPreference.addListener(applyMotionPreference);

  webglReady = initializeKeyRenderer();
  if (webglReady) applyMotionPreference();
  else fallbackToPoster();
}

const capabilityStage = aecRoot.querySelector(".theater-stage");
const capabilityChapters = Array.from(aecRoot.querySelectorAll(".capability-chapter"));
const capabilityScene = aecRoot.querySelector(".theater-scene");
const stageState = aecRoot.querySelector("#stage-state");
const stageTitle = aecRoot.querySelector("#stage-title");
const capabilityMedia = new Map();
const desktopCapabilityLayout = window.matchMedia("(min-width: 941px)");
let activeCapabilityKey = capabilityStage ? capabilityStage.dataset.capabilityStage : null;
let pendingCapabilityKey = null;
let capabilityChangeToken = 0;
let capabilityScrollFrame = 0;

Object.entries(capabilityStates).forEach(function ([key, state]) {
  const image = new Image();
  image.decoding = "async";
  image.alt = state.alt;
  const loaded = new Promise(function (resolve) {
    image.addEventListener("load", function () { resolve(true); }, { once: true });
    image.addEventListener("error", function () { resolve(false); }, { once: true });
  });
  image.src = state.image;
  const ready = typeof image.decode === "function"
    ? image.decode().then(function () { return true; }).catch(function () { return loaded; })
    : loaded;
  capabilityMedia.set(key, { image, ready });
});

async function setCapability(key) {
  const next = capabilityStates[key];
  if (!next || !capabilityStage) return;
  if (key === activeCapabilityKey || key === pendingCapabilityKey) return;

  pendingCapabilityKey = key;
  const changeToken = ++capabilityChangeToken;
  const media = capabilityMedia.get(key);
  const mediaReady = media ? await media.ready : true;

  if (!mediaReady || changeToken !== capabilityChangeToken || pendingCapabilityKey !== key) return;
  await new Promise(function (resolve) { window.requestAnimationFrame(resolve); });
  if (changeToken !== capabilityChangeToken || pendingCapabilityKey !== key) return;

  pendingCapabilityKey = null;
  activeCapabilityKey = key;
  if (capabilityScene && capabilityScene.getAttribute("src") !== next.image) {
    capabilityScene.src = next.image;
  }
  if (capabilityScene) capabilityScene.alt = next.alt;
  capabilityStage.dataset.capabilityStage = key;
  stageState.textContent = next.state;
  stageTitle.textContent = next.title;
  capabilityChapters.forEach(function (chapter) {
    const active = chapter.dataset.capability === key;
    chapter.classList.toggle("is-active", active);
    chapter.setAttribute("aria-selected", String(active));
  });
}

capabilityChapters.forEach(function (chapter) {
  chapter.addEventListener("click", function () { setCapability(chapter.dataset.capability); });
  chapter.addEventListener("keydown", function (event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setCapability(chapter.dataset.capability);
    }
  });
});

function updateCapabilityFromScroll() {
  capabilityScrollFrame = 0;
  if (!desktopCapabilityLayout.matches || capabilityChapters.length === 0) return;

  const focusLine = window.innerHeight * 0.5;
  let closestChapter = capabilityChapters[0];
  let closestDistance = Infinity;

  capabilityChapters.forEach(function (chapter) {
    const rect = chapter.getBoundingClientRect();
    const chapterCenter = rect.top + rect.height / 2;
    const distance = Math.abs(chapterCenter - focusLine);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestChapter = chapter;
    }
  });

  setCapability(closestChapter.dataset.capability);
}

function scheduleCapabilityUpdate() {
  if (capabilityScrollFrame) return;
  capabilityScrollFrame = window.requestAnimationFrame(updateCapabilityFromScroll);
}

window.addEventListener("scroll", scheduleCapabilityUpdate, { passive: true });
window.addEventListener("resize", scheduleCapabilityUpdate);
if (desktopCapabilityLayout.addEventListener) {
  desktopCapabilityLayout.addEventListener("change", scheduleCapabilityUpdate);
} else {
  desktopCapabilityLayout.addListener(scheduleCapabilityUpdate);
}
scheduleCapabilityUpdate();

const roleTabs = aecRoot.querySelectorAll(".role-tab");
const roleMedia = aecRoot.querySelector(".role-media");
const roleImage = aecRoot.querySelector("#role-image");
const roleMediaLabel = aecRoot.querySelector("#role-media-label");
const roleEyebrow = aecRoot.querySelector("#role-eyebrow");
const roleTitle = aecRoot.querySelector("#role-title");
const roleDescription = aecRoot.querySelector("#role-description");
const rolePoints = aecRoot.querySelector("#role-points");
const roleLink = aecRoot.querySelector("#role-link");
const roleMediaPreloads = Object.values(roles).map(function (role) {
  const image = new Image();
  image.decoding = "async";
  image.src = role.image;
  if (typeof image.decode === "function") image.decode().catch(function () {});
  return image;
});

function setRole(key) {
  const next = roles[key];
  if (!next) return;
  roleTabs.forEach(function (tab) {
    const active = tab.dataset.role === key;
    tab.classList.toggle("is-active", active);
    tab.setAttribute("aria-selected", String(active));
  });
  roleMedia.classList.add("is-changing");
  window.setTimeout(function () {
    roleImage.src = next.image;
    roleImage.alt = next.alt;
    roleImage.style.objectPosition = next.imagePosition || "center";
    roleMediaLabel.textContent = next.media;
    roleEyebrow.textContent = next.eyebrow;
    roleTitle.textContent = next.title;
    roleDescription.textContent = next.description;
    rolePoints.replaceChildren();
    next.points.forEach(function (point) {
      const item = document.createElement("li");
      item.textContent = point;
      rolePoints.appendChild(item);
    });
    roleLink.hidden = !next.link;
    if (next.link) {
      roleLink.href = next.link;
      roleLink.textContent = next.linkLabel + " →";
    } else {
      roleLink.removeAttribute("href");
      roleLink.textContent = "";
    }
    roleMedia.classList.remove("is-changing");
  }, 160);
}

roleTabs.forEach(function (tab) {
  tab.addEventListener("click", function () { setRole(tab.dataset.role); });
});

const outputTabs = aecRoot.querySelectorAll(".output-tab");
const outputVisuals = aecRoot.querySelectorAll(".output-visual");
const outputPreview = aecRoot.querySelector(".output-preview");
const outputKind = aecRoot.querySelector("#output-kind");
const outputTitle = aecRoot.querySelector("#output-title");
const outputDescription = aecRoot.querySelector("#output-description");
const outputMeta = aecRoot.querySelector("#output-meta");
const outputAction = aecRoot.querySelector("#output-action");
const outputVideos = aecRoot.querySelectorAll("[data-output-video]");

function setOutput(key) {
  const next = outputs[key];
  if (!next) return;
  outputTabs.forEach(function (tab) {
    const active = tab.dataset.output === key;
    tab.classList.toggle("is-active", active);
    tab.setAttribute("aria-selected", String(active));
  });
  outputVisuals.forEach(function (visual) {
    const active = visual.classList.contains("visual-" + key);
    visual.setAttribute("aria-hidden", String(!active));
  });
  aecRoot.querySelectorAll("realsee-vr-frame").forEach(function (frame) {
    frame.requestLoad();
  });
  outputVideos.forEach(function (video) {
    const active = video.dataset.outputVideo === key;
    if (!active) {
      video.pause();
      return;
    }
    if (video.readyState >= 1) video.currentTime = 0;
    const playRequest = video.play();
    if (playRequest) playRequest.catch(function () {});
  });
  outputPreview.dataset.outputStage = key;
  outputKind.textContent = next.kind;
  outputTitle.textContent = next.title;
  outputDescription.textContent = next.description;
  outputMeta.textContent = next.meta;
  outputAction.dataset.actionType = next.actionType;
  outputAction.dataset.sample = next.title;
  outputAction.dataset.downloadReady = String(Boolean(next.downloadUrl));
  outputAction.dataset.analysisEventAction = "click";
  outputAction.dataset.ppEvent = next.telemetryEvent;
  outputAction.dataset.ppUid = next.telemetryUid;
  outputAction.href = next.actionType === "external" ? next.actionUrl : (next.downloadUrl || "#");
  outputAction.toggleAttribute("target", next.actionType === "external");
  outputAction.toggleAttribute("rel", next.actionType === "external");
  if (next.actionType === "external") {
    outputAction.target = "_blank";
    outputAction.rel = "noopener noreferrer";
  }
  outputAction.toggleAttribute("download", next.actionType === "download" && Boolean(next.downloadUrl));
  if (next.actionType === "download" && next.downloadUrl) {
    outputAction.dataset.ppTargetUrl = next.downloadUrl;
    outputAction.dataset.ppActionType = "1";
  } else {
    delete outputAction.dataset.ppTargetUrl;
    delete outputAction.dataset.ppActionType;
  }
  outputAction.textContent = next.action;
}

outputTabs.forEach(function (tab) {
  tab.addEventListener("click", function () { setOutput(tab.dataset.output); });
});

const toast = aecRoot.querySelector("#toast");
let toastTimer;

function showToast(message) {
  toast.textContent = message;
  toast.hidden = false;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(function () { toast.hidden = true; }, 2800);
}

outputAction.addEventListener("click", function (event) {
  if (outputAction.dataset.downloadReady === "true") return;
  if (outputAction.dataset.actionType === "external") return;
  event.preventDefault();
  showToast(outputAction.dataset.sample + " sample is being prepared. Contact Realsee to request a current file.");
});

const workflowTabs = aecRoot.querySelectorAll(".workflow-tab");
const workflowVideos = aecRoot.querySelectorAll("[data-demo-video]");
const workflowVideoFrame = aecRoot.querySelector(".workflow-video-frame");
const workflowVideoToggle = aecRoot.querySelector("#workflow-video-toggle");
const demoEyebrow = aecRoot.querySelector("#demo-eyebrow");
const demoTitle = aecRoot.querySelector("#demo-title");
const demoCopy = aecRoot.querySelector("#demo-copy");
const workflowMotionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
let activeDemoKey = "compare";
let workflowVideoIsVisible = true;
let workflowVideoUserPaused = false;

function getActiveWorkflowVideo() {
  return aecRoot.querySelector('[data-demo-video="' + activeDemoKey + '"]');
}

function updateWorkflowVideoControl(video) {
  if (!workflowVideoToggle || !video) return;
  const paused = video.paused;
  const label = paused ? "Play workflow video" : "Pause workflow video";
  workflowVideoToggle.setAttribute("aria-label", label);
  workflowVideoToggle.setAttribute("title", paused ? "Play video" : "Pause video");
  const icon = workflowVideoToggle.querySelector("img");
  if (icon) icon.src = paused ? aecAsset("icon-play.svg") : aecAsset("icon-pause.svg");
}

function playActiveWorkflowVideo() {
  const video = getActiveWorkflowVideo();
  if (!video || workflowVideoUserPaused || !workflowVideoIsVisible || document.hidden || workflowMotionPreference.matches) {
    updateWorkflowVideoControl(video);
    return;
  }
  const playRequest = video.play();
  if (playRequest) playRequest.catch(function () { updateWorkflowVideoControl(video); });
}

function setDemo(key) {
  const next = demos[key];
  if (!next) return;
  activeDemoKey = key;
  workflowTabs.forEach(function (tab) {
    const active = tab.dataset.demo === key;
    tab.classList.toggle("is-active", active);
    tab.setAttribute("aria-selected", String(active));
  });
  workflowVideos.forEach(function (video) {
    const active = video.dataset.demoVideo === key;
    video.classList.toggle("is-active", active);
    video.setAttribute("aria-hidden", String(!active));
    if (active) {
      video.currentTime = 0;
    } else {
      video.pause();
    }
  });
  demoEyebrow.textContent = next.eyebrow;
  demoTitle.textContent = next.title;
  demoCopy.textContent = next.copy;
  workflowVideoUserPaused = false;
  playActiveWorkflowVideo();
}

workflowTabs.forEach(function (tab) {
  tab.addEventListener("click", function () { setDemo(tab.dataset.demo); });
});

workflowVideos.forEach(function (video) {
  video.addEventListener("play", function () {
    if (video.dataset.demoVideo === activeDemoKey) updateWorkflowVideoControl(video);
  });
  video.addEventListener("pause", function () {
    if (video.dataset.demoVideo === activeDemoKey) updateWorkflowVideoControl(video);
  });
});

if (workflowVideoToggle) {
  workflowVideoToggle.addEventListener("click", function () {
    const video = getActiveWorkflowVideo();
    if (!video) return;
    if (video.paused) {
      workflowVideoUserPaused = false;
      const playRequest = video.play();
      if (playRequest) playRequest.catch(function () { updateWorkflowVideoControl(video); });
    } else {
      workflowVideoUserPaused = true;
      video.pause();
    }
  });
}

if (workflowVideoFrame && "IntersectionObserver" in window) {
  const workflowVideoObserver = new IntersectionObserver(function (entries) {
    workflowVideoIsVisible = Boolean(entries[0] && entries[0].isIntersecting);
    const video = getActiveWorkflowVideo();
    if (!workflowVideoIsVisible && video) video.pause();
    else playActiveWorkflowVideo();
  }, { threshold: 0.08 });
  workflowVideoObserver.observe(workflowVideoFrame);
}

document.addEventListener("visibilitychange", function () {
  const video = getActiveWorkflowVideo();
  if (document.hidden && video) video.pause();
  else playActiveWorkflowVideo();
});

if (workflowMotionPreference.addEventListener) {
  workflowMotionPreference.addEventListener("change", function () {
    const video = getActiveWorkflowVideo();
    if (workflowMotionPreference.matches && video) video.pause();
    else playActiveWorkflowVideo();
  });
}

setDemo("compare");

function createImageTabbedController(options) {
  const mediaPreloads = new Map();
  let changeToken = 0;

  Object.entries(options.items).forEach(function ([key, item]) {
    const image = new Image();
    image.decoding = "async";
    const loaded = new Promise(function (resolve) {
      image.addEventListener("load", function () { resolve(true); }, { once: true });
      image.addEventListener("error", function () { resolve(false); }, { once: true });
    });
    image.src = item.image;
    const ready = typeof image.decode === "function"
      ? image.decode().then(function () { return true; }).catch(function () { return loaded; })
      : loaded;
    mediaPreloads.set(key, ready);
  });

  async function select(key) {
    const next = options.items[key];
    if (!next) return;
    const currentToken = ++changeToken;
    options.tabs.forEach(function (tab) {
      const active = tab.dataset[options.dataKey] === key;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", String(active));
    });
    options.stage.classList.add("is-changing");
    const ready = mediaPreloads.get(key);
    if (ready) await ready;
    if (currentToken !== changeToken) return;
    options.image.src = next.image;
    options.image.alt = next.alt;
    options.update(next);
    options.stage.classList.remove("is-changing");
  }

  options.tabs.forEach(function (tab) {
    tab.addEventListener("click", function () { select(tab.dataset[options.dataKey]); });
  });
}

const scenarioTabs = aecRoot.querySelectorAll(".scenario-tab");
const scenarioStage = aecRoot.querySelector(".scenario-stage");
const scenarioImage = aecRoot.querySelector("#scenario-image");
const scenarioMedia = aecRoot.querySelector("#scenario-media-label");
const scenarioRoles = aecRoot.querySelector("#scenario-roles");
const scenarioTitle = aecRoot.querySelector("#scenario-title");
const scenarioProblem = aecRoot.querySelector("#scenario-problem");
const scenarioSolution = aecRoot.querySelector("#scenario-solution");
const scenarioList = aecRoot.querySelector("#scenario-list");
createImageTabbedController({
  items: scenarios,
  tabs: scenarioTabs,
  dataKey: "scenario",
  stage: scenarioStage,
  image: scenarioImage,
  update: function (next) {
    scenarioMedia.textContent = next.media;
    scenarioMedia.classList.toggle("is-hidden", Boolean(next.hasEmbeddedMediaLabel));
    scenarioRoles.textContent = next.roles;
    scenarioTitle.textContent = next.title;
    scenarioProblem.textContent = next.problem;
    scenarioSolution.textContent = next.solution;
    scenarioList.replaceChildren();
    next.list.forEach(function (point) {
      const item = document.createElement("li");
      item.textContent = point;
      scenarioList.appendChild(item);
    });
  }
});

const specialtyTabs = aecRoot.querySelectorAll(".specialty-tab");
const specialtyDetail = aecRoot.querySelector(".specialty-detail");
const specialtyImage = aecRoot.querySelector("#specialty-image");
const specialtyVisualLabel = aecRoot.querySelector("#specialty-visual-label");
const specialtyKicker = aecRoot.querySelector("#specialty-kicker");
const specialtyTitle = aecRoot.querySelector("#specialty-title");
const specialtyCopy = aecRoot.querySelector("#specialty-copy");
const specialtyBoundary = aecRoot.querySelector("#specialty-boundary");
createImageTabbedController({
  items: specialties,
  tabs: specialtyTabs,
  dataKey: "specialty",
  stage: specialtyDetail,
  image: specialtyImage,
  update: function (next) {
    specialtyVisualLabel.classList.toggle("is-hidden", Boolean(next.hasEmbeddedVisualLabel));
    specialtyKicker.textContent = next.kicker;
    specialtyTitle.textContent = next.title;
    specialtyCopy.textContent = next.copy;
    specialtyBoundary.textContent = next.boundary;
  }
});

const customerGptFloat = aecRoot.querySelector(".customer-gpt-float");

if (customerGptFloat) {
  customerGptFloat.addEventListener("click", function () {
    const eventDetail = {
      event: customerGptFloat.dataset.event || "custom_gpt_click",
      placement: customerGptFloat.dataset.placement || "floating_bottom_right",
      page_path: window.location.pathname,
      locale: document.documentElement.lang || "en",
      device_type: window.matchMedia("(max-width: 680px)").matches ? "mobile" : "desktop",
      campaign: customerGptFloat.dataset.campaign || "aec_page"
    };

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(eventDetail);
    }

    window.dispatchEvent(new CustomEvent("custom_gpt_click", { detail: eventDetail }));
  });
}

if ("IntersectionObserver" in window && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
  aecRoot.classList.add("motion-ready");
  const revealObserver = new IntersectionObserver(function (entries, observer) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("is-visible");
      observer.unobserve(entry.target);
    });
  }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
  aecRoot.querySelectorAll(".reveal").forEach(function (element) { revealObserver.observe(element); });
}

function alignInitialAnchor() {
  const selector = window.location.hash;
  if (!selector) return;
  const target = aecRoot.querySelector(selector);
  if (!target) return;
  const root = document.documentElement;
  const previousBehavior = root.style.scrollBehavior;
  root.style.scrollBehavior = "auto";
  target.scrollIntoView({ block: "start" });
  root.style.scrollBehavior = previousBehavior;
}

window.addEventListener("load", function () {
  window.setTimeout(alignInitialAnchor, 80);
});

})();
